import { loadPatientTeamMessages } from "./patientTeamMessagesV4";
/* ============================================================== */
/*                                                                */
/*   ExamV4 sequence  ·  HIS V4 · หน้าตรวจรักษา                     */
/*                                                                */
/*   1) โหลดข้อมูลหน้าตรวจรักษาด้วย API ชุดเดียวกับ V3               */
/*   2) save ตรวจร่างกาย / วินิจฉัย / ภาพ / บันทึกการพยาบาล          */
/*   3) map payload API -> ExamV4Slice ให้ CardDoctorV4 ฯลฯ render  */
/*                                                                */
/*   domain type + mapper + slice : patient/examModelV4.ts           */
/*   กติกา UI ที่ pure (status/role gate) : patient/examDispatchV4.ts  */
/*   UI import ผ่าน common/commonV4Interface.ts                       */
/*                                                                */
/*   ใช้ framework เดียวกับ PatientOrdersV4 (team practice):          */
/*   HISV3/common/CommonInterface -> ActionHandler / GenerateActionTypes */
/*   / generatePrefixedActions / BLC_STATUS (key เดียวกับ SetErrorMessage) */
/*   loading -> state.buttonLoadCheck["<card>_<ACTION>"]              */
/*   error   -> state.errorMessage["<card>"]                          */
/*   slice ของ sequence เก็บเฉพาะข้อมูล ไม่เก็บ loading/error         */
/*                                                                */
/*   SDK ทุก module return tuple [data, error, network]              */
/*   ตัวนี้ตรวจครบ 3 ช่อง network error ไม่กลายเป็นข้อมูลว่าง        */
/*                                                                */
/* ============================================================== */

import WasmController from "react-lib/frameworks/WasmController";

// framework เดียวกับที่ team ใช้ใน common/sequence/PatientOrdersV4.ts
import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  type BLC_STATUS_TYPE,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  markButtonLoadCheck,
  seqBtnKey,
  seqErrorKey,
  writeSequenceState,
  type SeqMeta,
} from "./sequenceStateV4";
import { apiErrorTextOf, errorTextOf } from "./sequenceStateV4";

/* ---- APIs (ชุดที่ V3 ใช้จริง ดู comment ใน patient/examModelV4.ts) ---- */
import EncounterMedicalRecordList from "issara-sdk/apis/EncounterMedicalRecordList_core";
import PatientEMRList from "issara-sdk/apis/PatientEMRList_apps_DPO";
import PatientEMRNew from "issara-sdk/apis/PatientEMRNew_apps_DPO";
import PatientEMREdit from "issara-sdk/apis/PatientEMREdit_apps_DPO";
import PatientHistoryDetail from "issara-sdk/apis/PatientHistoryDetail_apps_DPO";
import EncounterActionOnly from "issara-sdk/apis/EncounterActionOnly_core";
import EncounterDPOCheckIn from "issara-sdk/apis/EncounterDPOCheckIn_apps_DPO";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";
import DoctorDischargePatient from "issara-sdk/apis/DoctorDischargePatient_apps_DPO";
import PhysicalExamOrganList from "issara-sdk/apis/PhysicalExamOrganList_apps_DPO";
import PhysicalExamTemplateList from "issara-sdk/apis/PhysicalExamTemplateList_apps_DPO";
import DiagnosisMedicalRecordDetail from "issara-sdk/apis/DiagnosisMedicalRecordDetail_apps_DPO";
import icd10KeyUp from "issara-sdk/apis/icd10KeyUp_core";
import MedicalRecordGalleryList from "issara-sdk/apis/MedicalRecordGalleryList_apps_DPO";
import EncounterHistoryList from "issara-sdk/apis/EncounterHistoryList_apps_DPO";
import MedicalRecordPictureDetail from "issara-sdk/apis/MedicalRecordPictureDetail_apps_DPO";
/* 86630 / Redmine #86780: ชั้นเส้นที่วาดบนภาพ เก็บแยกจากไฟล์ภาพต้นฉบับ */
import ClinicalPictureAnnotationList from "issara-sdk/apis/ClinicalPictureAnnotationList_apps_DPO";
import ClinicalPictureAnnotationDetail from "issara-sdk/apis/ClinicalPictureAnnotationDetail_apps_DPO";
import TemplateGalleryList from "issara-sdk/apis/TemplateGalleryList_apps_DPO";
import NurseNoteItemList from "issara-sdk/apis/NurseNoteItemList_apps_PTM";
import NurseNoteItemDetail from "issara-sdk/apis/NurseNoteItemDetail_apps_PTM";
/* variant ที่มี data ใน delete (apps_PTM.delete ไม่มี data -> ส่ง username/password ไม่ได้) */
import NurseNoteItemDetailWithBody from "issara-sdk/apis/NurseNoteItemDetail_apps_PTMM";
import NurseNoteLatest from "issara-sdk/apis/NurseNoteLatest_apps_PTM";
import NurseNoteTemplateItemList from "issara-sdk/apis/NurseNoteTemplateItemList_apps_PTM";
import NurseNoteTemplateList from "issara-sdk/apis/NurseNoteTemplateList_apps_PTM";
import NurseNoteTemplateItemDetail from "issara-sdk/apis/NurseNoteTemplateItemDetail_apps_PTM";
import TriageFromEncounter from "issara-sdk/apis/TriageFromEncounter_core";
import VitalSignValueList from "issara-sdk/apis/VitalSignValueList_apps_PTM";
import AdverseReactionList from "issara-sdk/apis/AdverseReactionList_apps_ADR";
import AdverseReactionDetail from "issara-sdk/apis/AdverseReactionDetail_apps_ADR";
import ProblemListList from "issara-sdk/apis/ProblemListList_apps_DPO";
import ProblemListDetail from "issara-sdk/apis/ProblemListDetail_apps_DPO";
/* sensitive note: ชื่อ Class ต่างจาก endpoint (…/apis/DPO/sensitive_note/emr/{emr}/) — V3 ก็ใช้ชื่อนี้ (DPOI.ts:18) */
import ClassifiedPatientHistoryDetail from "issara-sdk/apis/ClassifiedPatientHistoryDetail_apps_DPO";
import LoyaltyAccountListView from "issara-sdk/apis/LoyaltyAccountListView_apps_CRM";
import LoyaltyTierListView from "issara-sdk/apis/LoyaltyTierListView_apps_CRM";
import PatientAppointmentV2List from "issara-sdk/apis/PatientAppointmentV2List_apps_QUE";

/* ---- domain ---- */
import moment from "moment";

import {
  buildDiagnosisPayload,
  doctorIdOf,
  buildExamStartPayload,
  buildPatientHistoryPayload,
  hasHistoryValues,
  buildNurseNotePayload,
  buildNurseNoteUpdatePayload,
  buildPatientScreenPayload,
  buildPictureUpdatePayload,
  selectedTemplateRows,
  asDict,
  asNumber,
  asText,
  diagnosisFromApi,
  diagnosisApprovalFromApi,
  sensitiveNoteRecordFromApi,
  buildSensitiveNoteCreatePayload,
  buildSensitiveNoteUpdatePayload,
  needsSupervisorApproval,
  allergyListParams,
  problemListParams,
  buildAdrPayload,
  buildProblemPayload,
  ADR_ACTION_SAVE,
  ADR_ACTION_CANCEL,
  allergyItemsFromApi,
  problemItemsFromApi,
  examLoadFromApiPayloads,
  galleryFromApi,
  galleryHistoryEncountersFromApi,
  galleryHistoryGroupsOf,
  galleryTemplatesFromApi,
  icdHitsFromSearch,
  type IcdSearchMethod,
  itemsOf,
  memberFromApi,
  nextAppointmentFromApi,
  OPEN_APPOINTMENT_STATUS,
  termNamesOf,
  emptyExamV4Slice,
  emrOfEncounter,
  normalizeExamV4Slice,
  nurseNoteHeadIdFromApi,
  nurseNotesFromApi,
  nurseTemplatesFromApi,
  nurseFocusTemplatesFromApi,
  emptyNurseTemplateDraft,
  validateNurseTemplateDraft,
  nurseTemplateCreatePayload,
  nurseTemplateUpdatePayload,
  nurseTemplateSavedMessage,
  nurseTemplateDeletedMessage,
  friendlyNurseTemplateErrorText,
  type NurseTemplateDraft,
  type DiagnosisState,
  type ExamIds,
  type ExamOther,
  type ExamRow,
  type ExamV4Slice,
  type GalleryApiPhoto,
  type IcdHit,
  type NurseFieldKey,
  type RecordSaveFailure,
  type RecordSaveMode,
  type Subjective,
} from "../../patient/examModelV4";
import { checkoutEncounterOf, type CheckoutEncounter } from "../../patient/checkoutModelV4";

/* ---- กติกา UI ล้วน ๆ (status/role gate) ที่ไม่ต้องลาก SDK ---- */
import {
  ACTION_START_EXAM,
  CARD_NAMES,
  isCallInStatus,
  isCheckedOutStatus,
} from "../../patient/examDispatchV4";

/* ============================================================== */
/*   SECTION 1 · TYPE-DEFINING CONSTANTS                           */
/* ============================================================== */

export const SEQUENCE_NAME = "ExamV4" as const;
export const CARD_NAME = CARD_NAMES.doctor;

export const ACTIONS = {
  LOAD: "LOAD",
  LOAD_GALLERY: "LOAD_GALLERY",
  /* ประวัติภาพย้อนหลังของผู้ป่วย (86631): encounter-history ของ HN เดียวกัน + gallery ของแต่ละ emr */
  LOAD_GALLERY_HISTORY: "LOAD_GALLERY_HISTORY",
  LOAD_NURSE: "LOAD_NURSE",
  /* Patient Panel: ระดับสมาชิก + นัดครั้งหน้า */
  LOAD_PANEL: "LOAD_PANEL",
  START_EXAM: "START_EXAM",
  /* บันทึกร่าง / Confirm ของหน้าตรวจรักษา: ทำทุกส่วนตามลำดับใน action เดียวแล้วรายงานผลรายส่วน */
  SAVE_RECORD: "SAVE_RECORD",
  SAVE_HISTORY: "SAVE_HISTORY",
  /* บันทึกเฉพาะ (sensitive note) — V3 เปิด modal History แล้ว save พร้อมประวัติ (SubPatientHistory.tsx:183-210) */
  LOAD_SENSITIVE_NOTE: "LOAD_SENSITIVE_NOTE",
  SAVE_SENSITIVE_NOTE: "SAVE_SENSITIVE_NOTE",
  SEARCH_ICD10: "SEARCH_ICD10",
  UPLOAD_PHOTO: "UPLOAD_PHOTO",
  UPDATE_PHOTO: "UPDATE_PHOTO",
  DELETE_PHOTO: "DELETE_PHOTO",
  /* ย้อนกลับภาพที่ลบ (soft delete) — gallery list ไม่เคยคืนรายการที่ is_active=false จึงต้อง PATCH คืน */
  RESTORE_PHOTO: "RESTORE_PHOTO",
  /* 86630 เก็บเส้นเป็น layer (POST clinical-picture-annotation) ไม่แก้ไฟล์ภาพต้นฉบับ */
  SAVE_PHOTO_ANNOTATIONS: "SAVE_PHOTO_ANNOTATIONS",
  DELETE_PHOTO_ANNOTATIONS: "DELETE_PHOTO_ANNOTATIONS",
  /* รูปแบบภาพของคลินิก (V3: getTemplateGalleryImage -> ModalTemplateFile) */
  LOAD_GALLERY_TEMPLATES: "LOAD_GALLERY_TEMPLATES",
  SAVE_NURSE_NOTE: "SAVE_NURSE_NOTE",
  /* 86637: สร้าง/แก้ Focus template (NurseNoteTemplate + item) ผ่าน API ของ PTM */
  SAVE_NURSE_TEMPLATE: "SAVE_NURSE_TEMPLATE",
  DELETE_NURSE_TEMPLATE: "DELETE_NURSE_TEMPLATE",
  UPDATE_NURSE_NOTE: "UPDATE_NURSE_NOTE",
  DELETE_NURSE_NOTE: "DELETE_NURSE_NOTE",
  /* flag ของ Patient Panel: ประวัติแพ้ (ADR) + โรคประจำตัว (problem list) */
  SAVE_ADR: "SAVE_ADR",
  UPDATE_ADR: "UPDATE_ADR",
  CANCEL_ADR: "CANCEL_ADR",
  SAVE_PROBLEM: "SAVE_PROBLEM",
  UPDATE_PROBLEM: "UPDATE_PROBLEM",
  INACTIVATE_PROBLEM: "INACTIVATE_PROBLEM",
  /* Check out ของ Encounter (Patient Panel) */
  CHECK_OUT: "CHECK_OUT",
  CANCEL_CHECK_OUT: "CANCEL_CHECK_OUT",
} as const;

/** key ใน state.buttonLoadCheck เช่น CardDoctorV4_LOAD_GALLERY (แบบเดียวกับ PatientOrdersV4.BTN_ACTS) */
export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** card ที่เป็นผู้ส่ง action (doctor / gallery / nursing) */
export { ACTION_START_EXAM, CARD_NAMES };

/* ============================================================== */
/*   SECTION 2 · STATE                                             */
/* ============================================================== */

export type State = Partial<{
  /**
   * Partial<ExamV4Slice> ไม่ใช่ ExamV4Slice เพราะ RunSequence เขียน `{ sequenceIndex: "START" }`
   * ลง key นี้ก่อนเรียก START handler (MainHISInterface.ts:700-712)
   * ถ้าบอกว่าเป็น slice สมบูรณ์ TS จะไม่บังคับให้ normalize ที่ฝั่งอ่าน (เคยเกิด .length พังจริง)
   */
  ExamV4Sequence: Partial<ExamV4Slice> | null;
}>;

export const StateInitial: State = { ExamV4Sequence: null };

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "HandleRefreshEncounter"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export const DataInitial: Data = {};

export type Props = {};

/** slice state ที่ MainHIS ส่งลงมาให้ card */
export type ExamV4SequenceState = ExamV4Slice;

/* ============================================================== */
/*   SECTION 3 · PARAMS TYPE                                       */
/* ============================================================== */

type Dict = { [key: string]: unknown };

/** ids ที่ card ส่งมา (หรือ sequence resolve เองจาก shared state) */
export type ExamIdsInput = Partial<ExamIds> & { ids?: ExamIds };

export type SeqState = BaseSeqState<SequenceName> &
  ExamIdsInput & { card?: string; action?: string; currentStatus?: string };

export type ActionPayloadMap = {
  /* currentStatus = สถานะ encounter ที่ caller รู้ (MainHISV4 / card ส่งมา) ใช้ตัดสินใจ auto check-in
     ถ้าไม่ส่งมา อ่านจาก state.selectedEncounter ของ MainHIS เอาไว้เอง */
  [ACTIONS.LOAD]: ExamIdsInput & {
    currentStatus?: string;
    /** สั่งอ่านกลับโดยไม่ขยับสถานะ (save ล้ม / StartExam เรียก check-in เองแล้ว) */
    skipCheckIn?: boolean;
  };
  [ACTIONS.LOAD_GALLERY]: ExamIdsInput;
  [ACTIONS.LOAD_GALLERY_HISTORY]: ExamIdsInput;
  [ACTIONS.LOAD_NURSE]: ExamIdsInput;
  [ACTIONS.LOAD_PANEL]: ExamIdsInput;
  [ACTIONS.START_EXAM]: ExamIdsInput & { currentStatus?: string };
  [ACTIONS.SAVE_RECORD]: ExamIdsInput & {
    /** สถานะ encounter ก่อนบันทึก — ExamV4 ใช้ตัดสิน auto check-in (V3) */
    currentStatus?: string;
    /** card ใช้จับคู่ผลใน slice.recordSave กับคำขอของตัวเอง */
    requestId?: string;
    mode?: RecordSaveMode;
    subjective?: Subjective;
    rows?: ExamRow[];
    /** แถวของ template ที่เลือก (ไม่ส่งมา = ใช้ค่าเดิมใน slice) */
    templateRows?: ExamRow[];
    other?: ExamOther;
    diagnosis?: DiagnosisState;
    /** credential ของแพทย์ผู้ดูแล (V3 ModAuthen ตอน ผู้ใช้เป็นนักศึกษา) */
    approvedByUsername?: string;
    approvedByPassword?: string;
    currentUserId?: number;
  };
  [ACTIONS.SAVE_HISTORY]: ExamIdsInput & { subjective?: Subjective };
  [ACTIONS.LOAD_SENSITIVE_NOTE]: ExamIdsInput;
  [ACTIONS.SAVE_SENSITIVE_NOTE]: ExamIdsInput & { sensitiveNote?: string };
  [ACTIONS.SEARCH_ICD10]: ExamIdsInput & { method: IcdSearchMethod; search: string };
  [ACTIONS.UPLOAD_PHOTO]: ExamIdsInput & { image: string; description?: string };
  [ACTIONS.UPDATE_PHOTO]: ExamIdsInput & { photoId: number; image?: string; description?: string };
  [ACTIONS.DELETE_PHOTO]: ExamIdsInput & { photoId: number };
  /* 86630: batch ของชั้นเส้นที่วาดใน popup (items = [{picture, type, color, width, points, display_seq}]) */
  [ACTIONS.SAVE_PHOTO_ANNOTATIONS]: ExamIdsInput & { photoId: number; items: Dict[] };
  /* ลบชั้นเส้น (ย้อนกลับ/ล้างเส้น) — backend soft delete ทีละ pk แล้วอ่าน gallery กลับครั้งเดียว */
  [ACTIONS.DELETE_PHOTO_ANNOTATIONS]: ExamIdsInput & { annotationIds: number[] };
  [ACTIONS.RESTORE_PHOTO]: ExamIdsInput & { photoId: number };
  [ACTIONS.LOAD_GALLERY_TEMPLATES]: ExamIdsInput & Record<string, never>;
  [ACTIONS.SAVE_NURSE_NOTE]: ExamIdsInput & {
    date?: string;
    values?: Partial<Record<NurseFieldKey, string>>;
    username?: string;
    password?: string;
    workingShift?: string;
    doctorOrderId?: number | string | null;
  };
  [ACTIONS.UPDATE_NURSE_NOTE]: ExamIdsInput & {
    noteId: number;
    values?: Partial<Record<NurseFieldKey, string>>;
    username?: string;
    password?: string;
    workingShift?: string;
    doctorOrderId?: number | string | null;
  };
  [ACTIONS.DELETE_NURSE_NOTE]: ExamIdsInput & {
    noteId: number;
    username?: string;
    password?: string;
  };
  /* 86637: draft.itemId === null = สร้างใหม่ (POST nurse-note-template/) ไม่งั้น PATCH item */
  [ACTIONS.SAVE_NURSE_TEMPLATE]: ExamIdsInput & { draft?: NurseTemplateDraft };
  [ACTIONS.DELETE_NURSE_TEMPLATE]: ExamIdsInput & { itemId: number; name?: string };
  [ACTIONS.SAVE_ADR]: ExamIdsInput & { note?: string; adversaryType?: number };
  [ACTIONS.UPDATE_ADR]: ExamIdsInput & { adrId: number; note?: string };
  [ACTIONS.CANCEL_ADR]: ExamIdsInput & { adrId: number; note?: string };
  [ACTIONS.SAVE_PROBLEM]: ExamIdsInput & { message?: string };
  [ACTIONS.UPDATE_PROBLEM]: ExamIdsInput & { problemId: number; message?: string };
  [ACTIONS.INACTIVATE_PROBLEM]: ExamIdsInput & { problemId: number };
  [ACTIONS.CHECK_OUT]: ExamIdsInput & {
    checkoutCause: string;
    note?: string;
    /** "DD/MM/พ.ศ.-HH:mm" เฉพาะ DEAD */
    deathDatetime?: string;
    onDone?: (ok: boolean) => void;
  };
  [ACTIONS.CANCEL_CHECK_OUT]: ExamIdsInput & { note: string; onDone?: (ok: boolean) => void };
};

type SequenceName = typeof SEQUENCE_NAME;
type Actions = typeof ACTIONS;
export type ActionName = Actions[keyof Actions];

type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

/** action object ที่ SequencePattern ส่งเข้ามาใน Action */
type ActionType = ActionTypes["ActionType"];

/** signature ของ function ที่ card เรียก (MainHIS supply this.runSequence) */
export type RunSequence = ActionTypes["RunSequence"];

/** alias ให้ UI/MainHISV4 อ้างอิงได้ตรงชื่อ */
export type ExamRunSequence = RunSequence;

/** params แบบกว้างที่ card ประกอบเอง (payload ของบาง action TS ตรวจไม่ครบเพราะ spread) */
export type ExamRunParamsLoose = {
  sequence: string;
  action: string;
  card?: string;
  restart?: boolean;
  clear?: boolean;
  [key: string]: unknown;
};

/**
 * type ของ prop runSequence ที่ MainHISV4 ส่งลง card
 * (MainHIS.tsx:1451 runSequence = (params: any) => handleEvent RunSequence จึงรับ params กว้างได้)
 */
export type ExamRunSequenceProp = (params: ExamRunParams | ExamRunParamsLoose) => void;

/**
 * params ที่ card ต้องส่งให้ runSequence
 * (MainHISV4 ส่ง `runSequence` ของ MainHIS ลงมาตรง ๆ หรือ adapter ใน test)
 */
export type ExamRunParams = {
  [K in ActionName]: {
    sequence: typeof SEQUENCE_NAME;
    action: K;
    /** card ผู้ส่ง = key ของ buttonLoadCheck / errorMessage */
    card?: string;
    restart?: boolean;
    clear?: boolean;
  } & ActionPayloadMap[K];
}[ActionName];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type ActionParams<A extends ActionName> = ActionTypes["Params"][A];

/** payload + ids/card ที่ Action inject ให้ทุก handler */
type Ctx<A extends ActionName> = ExamIdsInput & {
  ids: ExamIds;
  card?: string;
  action?: A;
  currentStatus?: string;
  skipCheckIn?: boolean;
  requestId?: string;
  mode?: RecordSaveMode;
  subjective?: Subjective;
  rows?: ExamRow[];
  templateRows?: ExamRow[];
  other?: ExamOther;
  diagnosis?: DiagnosisState;
  /** บันทึกเฉพาะ (sensitive note) ที่ผู้ใช้พิมพ์ใน modal History */
  sensitiveNote?: string;
  method?: IcdSearchMethod;
  search?: string;
  image?: string;
  description?: string;
  photoId?: number;
  items?: Dict[];
  annotationIds?: number[];
  noteId?: number;
  date?: string;
  values?: Partial<Record<NurseFieldKey, string>>;
  /* ผู้บันทึกของ nurse note (backend ต้องได้) */
  username?: string;
  password?: string;
  /* working_shift / doctor_order_id ของ nurse note (V3 PTMI.ts:793-810) */
  workingShift?: string;
  doctorOrderId?: number | string | null;
  /* 86637 Focus template */
  draft?: NurseTemplateDraft;
  itemId?: number;
  name?: string;
  /* flag ของ Patient Panel */
  adrId?: number;
  problemId?: number;
  note?: string;
  message?: string;
  adversaryType?: number;
  /* credential ของแพทย์ผู้ดูแล (อนุมัติวินิจฉัย) + user id ที่ login อยู่ */
  approvedByUsername?: string;
  approvedByPassword?: string;
  currentUserId?: number;
  /* Check out */
  checkoutCause?: string;
  deathDatetime?: string;
  onDone?: (ok: boolean) => void;
};

type CombinedState = PickedState & State;

export type Handler<P = unknown, R = void> = ActionHandler<CombinedState, Event, Data, P, R>;

type Ctl = WasmController<CombinedState, Event, Data, Props>;

/** division/device ที่ SDK ต้องการใน every request (extra ของ WasmController.data) */
const extraOf = (controller: Ctl) => ({
  division: controller.data?.division,
  device: controller.data?.device,
});



/* ============================================================== */
/*   SECTION 4 · INTERNAL HELPERS                                  */
/* ============================================================== */

type ApiTuple = [unknown, unknown, unknown];

const errorMessageOf = (result: ApiTuple): string | null => {
  /* errorTextOf จัดการทั้ง string / ArrayBuffer / DRF dict (ดู patient/examModelV4.ts)
     apiErrorTextOf ตรวจ axios error ด้วย isAxiosError ไม่ใช่ truthiness ของช่องที่ 3
     (tuple ฉบับ BaseService: [responseData, errorBody, axiosResult]) */
  return apiErrorTextOf(result, { fallbackText: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" });
};

const dataOf = (result: ApiTuple): unknown => result[0];

const sliceOf = (controller: Ctl): ExamV4Slice =>
  /* state อาจถูก RunSequence ล้างเหลือแค่ { sequenceIndex: "START" } ก่อน START handler ทำงาน
     (MainHISInterface.ts:700-712) จึง normalize ก่อนอ่านเสมอ */
  normalizeExamV4Slice(controller.getState().ExamV4Sequence);

/** ผลของ encounter ก่อนหน้ามาถึงช้า หลังผู้ใช้เปลี่ยนไปผู้ป่วยคนใหม่แล้ว -> ห้ามเขียนทับ */
const isStaleEncounter = (controller: Ctl, ids: ExamIds): boolean => {
  const current = sliceOf(controller).ids.encounterId;
  return current !== undefined && ids.encounterId !== undefined && current !== ids.encounterId;
};

/** meta ที่ handler ส่งมา (sequence name เพิ่มให้เองใน metaOf) */
type SliceMeta = { card?: string; action?: string };

const metaOf = (meta: SliceMeta): SeqMeta => ({
  sequence: SEQUENCE_NAME,
  card: meta.card ?? CARD_NAME,
  action: meta.action,
});

/** key สำหรับ debug/test: <card>_<action> (format เดียวกับ CommonInterface.SetErrorMessage) */
export const examBtnKey = (meta: SliceMeta): string => seqBtnKey(metaOf(meta));
export const examErrorKey = (meta: SliceMeta): string => seqErrorKey(metaOf(meta));

/** loading/saving/error ไม่อยู่ใน slice อีกต่อไป -> ย้ายไป state กลางของ app */
type WritePatch = Partial<ExamV4Slice> & {
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
};

/**
 * เขียน slice + buttonLoadCheck + errorMessage ด้วย setState ครั้งเดียว
 * (ถ้าเรียก SetErrorMessage ตามด้วยการ setState ของ slice จะ spread จาก state ที่ยัง batch cũ
 *  และเขียนทับ key กัน — team ใช้ SetErrorMessage เฉพาะจุดที่เขียน error อย่างเดียว)
 * key/format เดียวกับ HISV3/common/CommonInterface.ts (SetErrorMessage + BLC_STATUS)
 */
const writeSlice = (controller: Ctl, meta: SliceMeta, patch: WritePatch): any =>
  writeSequenceState<ExamV4Slice>(
    controller,
    metaOf(meta),
    "ExamV4Sequence",
    sliceOf(controller),
    { ...patch, sequenceIndex: "Action" },
    emptyExamV4Slice,
  );

/**
 * เขียน error ลง state.errorMessage[<card>] + buttonLoadCheck[<card>_<ACTION>] = ERROR
 *
 * ไม่ได้เรียก CommonInterface.SetErrorMessage ตรง ๆ เพราะ helper นั้น
 * ส่ง error เข้า TextDecoder.decode() (CommonInterface.ts:929) ซึ่งรับเฉพาะ ArrayBuffer/TypedArray
 * ถ้าส่งข้อความที่ sequence แปลไว้แล้ว (string) จะ TypeError ในเบราว์เซอร์
 * key ที่ได้เหมือนกันทุกตัว จึงใช้ state กลางแบบเดียวกัน
 */
const reportError = (controller: Ctl, meta: SliceMeta, message: string): void =>
  writeSlice(controller, meta, { error: message });

/** ตั้ง buttonLoadCheck หลาย key พร้อมกัน (Start โหลด 3 หน้าพร้อมกัน) */
const setBtn = (controller: Ctl, metas: SliceMeta[], status: BLC_STATUS_TYPE): void =>
  markButtonLoadCheck(controller, metas.map(metaOf), status);

/**
 * ids มาจาก 3 ที่ เรียงตามลำดับความใหม่
 *   1) params ที่ card ส่งมา
 *   2) slice เดิม (Start เคย resolve emrId ไว้)
 *   3) shared state ของ MainHIS (selectedEncounter / selectedEmr / selectedPatient)
 */
const readIds = (controller: Ctl, params: ExamIdsInput): ExamIds => {
  const slice = sliceOf(controller);
  const app = controller.getState() as {
    selectedEncounter?: { id?: unknown; patient_id?: unknown; doctor?: unknown };
    selectedEmr?: { id?: unknown; doctor?: unknown; encounter?: unknown };
    selectedPatient?: { patient_id?: unknown; id?: unknown };
  };
  const fromParams: Partial<ExamIds> = params.ids ?? params;

  const encounterId =
    fromParams.encounterId ?? slice.ids.encounterId ?? numberOrUndefined(app.selectedEncounter?.id);

  /* slice กับ shared state อาจยังเป็นของผู้ป่วยคนก่อน (SelectEncounter ยังโหลดไม่เสร็จ)
     ใช้ค่าจากสองที่นี้เฉพาะเมื่อเป็น encounter เดียวกันเท่านั้น */
  const sliceIds: ExamIds = slice.ids.encounterId === encounterId ? slice.ids : {};
  const appEncounter =
    numberOrUndefined(app.selectedEncounter?.id) === encounterId ? app.selectedEncounter : undefined;
  const appEmr = emrOfEncounter(app.selectedEmr, { id: encounterId });

  return {
    encounterId,
    emrId: fromParams.emrId ?? sliceIds.emrId ?? numberOrUndefined(appEmr?.id),
    patientId:
      fromParams.patientId ??
      sliceIds.patientId ??
      (appEncounter ? numberOrUndefined(app.selectedPatient?.patient_id) : undefined) ??
      numberOrUndefined(appEncounter?.patient_id),
    doctorId:
      fromParams.doctorId !== undefined
        ? fromParams.doctorId
        : sliceIds.doctorId ?? numberOrUndefined(appEmr?.doctor) ?? numberOrUndefined(appEncounter?.doctor),
  };
};

const numberOrUndefined = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
  return undefined;
};

/** emr id ของ encounter (EncounterMedicalRecordList_core) — V3 ใช้ตัวเดียวกัน (CardPatientListInterface.ts:714) */
const resolveEmrId = async (
  controller: Ctl,
  { ids }: { ids: ExamIds },
): Promise<{ ids: ExamIds; error: string | null }> => {
  if (ids.emrId) return { ids, error: null };
  if (!ids.encounterId) return { ids, error: "ยังไม่ได้เลือกผู้ป่วย (encounter)" };

  const result = await EncounterMedicalRecordList.list({
    pk: ids.encounterId,
    apiToken: controller.apiToken,
  });
  const error = errorMessageOf(result);
  if (error) return { ids, error };

  const emr = itemsOf(dataOf(result))[0];
  if (!emr) return { ids, error: "ไม่พบเวชระเบียน (emr) ของ encounter นี้" };

  return {
    ids: {
      ...ids,
      emrId: Number(emr.id),
      doctorId: ids.doctorId ?? numberOrUndefined(emr.doctor),
    },
    error: null,
  };
};

/** สถานะ encounter ที่ฝั่ง caller รู้ (params -> state.selectedEncounter ของ MainHIS) "" = ไม่รู้ */
const encounterStatusKnown = (controller: Ctl, ids: ExamIds, currentStatus?: string): string => {
  const fromParams = typeof currentStatus === "string" ? currentStatus.trim() : "";
  if (fromParams !== "") return fromParams;
  const state = controller.getState() as { selectedEncounter?: { id?: unknown; status?: unknown } };
  const selected = state.selectedEncounter;
  if (selected && numberOrUndefined(selected.id) === ids.encounterId) return String(selected.status ?? "").trim();
  return "";
};

/**
 * V3 ไม่ได้เปลี่ยน IN_QUEUE -> IN_EXAM ("กำลังพบแพทย์") จากปุ่มไหนเลย
 * แต่เกิดเป็น side effect ตอนอ่านเวชระเบียน: GET /apis/DPO/medical_record/emr/{emr}/
 * ถ้าไม่ส่ง auto_checkin=false -> backend ตั้ง encounter.action = OPD_CHECK_IN เอง
 *   DPO/views.py:604 (auto_checkin default True) + :800-821 (IN_QUEUE -> check in)
 *   V3 เรียกอ่านซ้ำหลัง save patient info (CardPatientInfo.tsx:267,525-528)
 *   และตอนแพทย์เปิด encounter ที่มี patient_illness แล้ว (CardPatientListInterface.ts:856-872)
 *
 * V4 อ่านเวชระเบียนด้วย auto_checkin=false เสมอ (กันการโหลดหน้า/สลับ tab ทำสถานะเปลี่ยนเงียบ)
 * จึงสั่ง check-in เองในจังหวะเดียวกับที่ V3 ทำ ผ่าน endpoint ตรงของ backend:
 *   PUT /apis/DPO/encounter-opd-check-in/{encounter}/ (DPO/urls.py:47, views.EncounterDPOCheckIn)
 * backend ขยับเฉพาะ encounter ที่ถึงขั้นเรียกเข้าตรวจได้จริง นอกนั้น return ตามสถานะเดิม
 *
 * ไม่ต้อง gate ด้วยบทบาทแพทย์ในจังหวะอัตโนมัติ (V3 ก็ไม่ gate — backendตัดสินสิทธิ์)
 * ยืนยันกับ local API แล้ว: ผู้ใช้ที่ ไม่ใช่ role DOCTOR (ทนพญ. นักเทคนิคการแพทย์) ซึ่งเป็น doctor
 * ของ encounter เรียก PUT /apis/DPO/encounter-opd-check-in/9850/ -> 200 {"status":"IN_EXAM"}
 * ถ้า backend ไม่อนุญาต (403/400) จะกลับมาเป็น error ในการ์ด ไม่รายงานว่าเปลี่ยนสถานะสำเร็จ
 * ส่วน "ปุ่มเรียกผู้ป่วยเข้าตรวจ" ในคิว/แบนเนอร์ยัง gate ด้วย isDoctorRole ของ MainHISV4 เหมือน V3
 * (CardPatientListInterface.ts:854)
 */
const checkInExamIfNeeded = async (
  controller: Ctl,
  {
    ids,
    currentStatus,
    force = false,
  }: { ids: ExamIds; currentStatus?: string; force?: boolean },
): Promise<{ changed: boolean; error: string | null }> => {
  const encounterId = ids.encounterId;
  if (encounterId === undefined) return { changed: false, error: null };
  if (!force && !isCallInStatus(encounterStatusKnown(controller, ids, currentStatus))) {
    return { changed: false, error: null };
  }

  const result = await EncounterDPOCheckIn.update({
    pk: encounterId,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error !== null) return { changed: false, error: `เรียกผู้ป่วยเข้าตรวจไม่สำเร็จ: ${error}` };

  /* backend ตอบสถานะกลับมาเสมอ (EncounterDPOCheckIn return {id, status})
     ไม่เรียก HandleRefreshEncounter เมื่อสถานะไม่ได้เปลี่ยน (กัน parent reload เยอร์) */
  const serverStatus = String(asDict(dataOf(result)).status ?? "").trim();
  const before = encounterStatusKnown(controller, ids, currentStatus);
  const changed = serverStatus === "" || serverStatus !== before;
  if (changed) {
    /* ให้ shared state (คิว / Patient Panel / หัวข้อหน้า) เห็น "กำลังพบแพทย์" ทันที */
    controller.handleEvent({ message: "HandleRefreshEncounter", params: { encounterId } });
  }
  return { changed, error: null };
};

/* ============================================================== */
/*   SECTION 5 · LOAD                                              */
/* ============================================================== */

/**
 * โหลดส่วนหลักของหน้าตรวจรักษา 1 รอบ
 *   GET  /apis/DPO/medical_record/emr/{emr}/        subjective + exam ที่เก็บไว้
 *   GET  /apis/DPO/physical_exam_organ/             master ระบบตรวจ
 *   GET  /apis/DPO/physical-exam-template/          template ของหน่วยตรวจ
 *   GET  /apis/DPO/medical_record/{emr}/diagnosis/  วินิจฉัย principal/secondary
 *   GET  /apis/core/triage/encounter/{encounter}/   อาการนำ + level จากการคัดกรอง
 *   GET  /apis/PTM/vital-sign-value/                vital sign ล่าสุด
 */
const LoadExam: Handler<Ctx<"LOAD">> = async (controller, params) => {
  const { ids } = params;

  if (!ids.emrId) {
    return writeSlice(controller, params, { ids, loading: false, error: "ไม่พบเวชระเบียน (emr) ของ encounter นี้" });
  }

  const emrId = ids.emrId as number;

  const [
    emrResult,
    organResult,
    templateResult,
    diagnosisResult,
    triageResult,
    vitalResult,
    allergyResult,
    problemResult,
  ] = await Promise.all([
      PatientEMRList.get({
        emr: emrId,
        params: { auto_checkin: false },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
      PhysicalExamOrganList.list({
        params: {},
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
      PhysicalExamTemplateList.list({
        params: {},
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
      DiagnosisMedicalRecordDetail.retrieve({
        pk: emrId,
        params: {},
        apiToken: controller.apiToken,
      }),
      ids.encounterId
        ? TriageFromEncounter.retrieve({ pk: ids.encounterId, apiToken: controller.apiToken })
        : Promise.resolve([null, null, null] as ApiTuple),
      ids.patientId
        ? VitalSignValueList.list({
            params: { patient: ids.patientId, latest_measurement: true },
            apiToken: controller.apiToken,
            extra: extraOf(controller),
          })
        : Promise.resolve([null, null, null] as ApiTuple),
      /* flag สองอันของ Patient Panel (ประวัติแพ้ / โรคประจำตัว) params ตรงกับ V3
         (CardPatientInfo.tsx:214-220 exclude_unused + severe_first + approve_only) */
      ids.patientId
        ? AdverseReactionList.list({
            params: allergyListParams(ids.patientId),
            apiToken: controller.apiToken,
            extra: extraOf(controller),
          })
        : Promise.resolve([null, null, null] as ApiTuple),
      ids.patientId
        ? ProblemListList.list({
            params: problemListParams(ids.patientId),
            apiToken: controller.apiToken,
            extra: extraOf(controller),
          })
        : Promise.resolve([null, null, null] as ApiTuple),
    ]);

  if (isStaleEncounter(controller, ids)) return undefined;

  /* กันข้อมูลผิดคน: EMR ที่อ่านได้ต้องเป็นของ encounter ที่เปิดอยู่ ไม่งั้นห้ามแสดงและห้ามบันทึกลงไป */
  const emrEncounterId = asNumber(asDict(dataOf(emrResult)).encounter_id);
  if (emrEncounterId !== undefined && ids.encounterId !== undefined && emrEncounterId !== ids.encounterId) {
    return writeSlice(controller, params, {
      ids: { ...ids, emrId: undefined },
      loading: false,
      error: "เวชระเบียนไม่ตรงกับผู้ป่วยที่เลือก กรุณาเปิดผู้ป่วยใหม่อีกครั้ง",
    });
  }

  const emrFailure = errorMessageOf(emrResult);
  const emrError = emrFailure === null ? null : `อ่านเวชระเบียนไม่สำเร็จ: ${emrFailure}`;
  const diagnosisFailure = errorMessageOf(diagnosisResult);
  const diagnosisError = diagnosisFailure === null ? null : `อ่านวินิจฉัยไม่สำเร็จ: ${diagnosisFailure}`;

  /* mapping ทั้งหมดอยู่ใน patient/examModelV4.ts (pure) เพื่อ lock ด้วย jest test ได้
     payload ของ list endpoint เป็น { items: [...] } เสมอ (V3 อ่าน data.items) */
  const loaded = examLoadFromApiPayloads({
    emr: dataOf(emrResult),
    organMaster: dataOf(organResult),
    templates: dataOf(templateResult),
    diagnosis: dataOf(diagnosisResult),
    triage: dataOf(triageResult),
    vitals: dataOf(vitalResult),
    allergy: dataOf(allergyResult),
    problems: dataOf(problemResult),
  });

  /* V3: เปิดหน้าตรวจรักษาของผู้ป่วยที่มี patient_illness แล้ว -> ขยับเป็น IN_EXAM เอง
     ยังไม่มีประวัติ (newInfo ของ V3) = ยังไม่ขยับ จะขยับหลังบันทึกครั้งแรก
     skipCheckIn = ผู้เรียกสั่งอ่านกลับโดยไม่เปลี่ยนสถานะ */
  const checkIn =
    emrFailure === null &&
    loaded.subjective.illnessId !== null &&
    params.skipCheckIn !== true
      ? await checkInExamIfNeeded(controller, { ids, currentStatus: params.currentStatus })
      : { changed: false, error: null };

  writeSlice(controller, params, {
    ids,
    loading: false,
    error: emrError ?? diagnosisError ?? checkIn.error,
    ...loaded,
  });
};

const LoadGallery: Handler<Ctx<"LOAD_GALLERY">> = async (controller, params) => {
  const { ids } = params;
  if (!ids.emrId) return writeSlice(controller, params, { ids, error: "ยังไม่มีเวชระเบียนสำหรับโหลดภาพ" });

  const result = await MedicalRecordGalleryList.list({
    emr: ids.emrId as number,
    apiToken: controller.apiToken,
  });
  if (isStaleEncounter(controller, ids)) return undefined;

  const error = errorMessageOf(result);

  writeSlice(controller, params, {
    ids,
    photos: error ? [] : galleryFromApi(dataOf(result)),
    error,
  });
};

/**
 * ประวัติภาพย้อนหลังของผู้รับบริการ (issue 86631)
 *   GET /apis/DPO/encounter-history/?patient={patient}   (encounter อทั้งหมดของผู้ป่วย, medical_record = emr id)
 *   GET /apis/DPO/medical_record/gallery/{emr}/          แต่ละ encounter (จำกัด 10 encounter ล่าสุด)
 * gallery API ผูกกับ emr เท่านั้น ไม่มี endpoint รวมทุก encounter ของผู้ป่วย
 */
const LoadGalleryHistory: Handler<Ctx<"LOAD_GALLERY_HISTORY">> = async (controller, params) => {
  const { ids } = params;
  if (!ids.patientId) {
    return writeSlice(controller, params, {
      ids,
      galleryHistory: [],
      error: "ยังไม่มี id ผู้ป่วย ไม่สามารถอ่านประวัติภาพย้อนหลังได้",
    });
  }

  const history = await EncounterHistoryList.list({
    params: { patient: ids.patientId },
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  if (isStaleEncounter(controller, ids)) return undefined;

  const historyError = errorMessageOf(history);
  if (historyError !== null) {
    return writeSlice(controller, params, { ids, galleryHistory: [], error: historyError });
  }

  const encounters = galleryHistoryEncountersFromApi(dataOf(history), ids.encounterId);
  const withEmr = encounters.filter((row) => row.emrId !== null);

  const galleries = await Promise.all(
    withEmr.map((row) =>
      MedicalRecordGalleryList.list({
        emr: row.emrId as number,
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
  );
  if (isStaleEncounter(controller, ids)) return undefined;

  const photosByEncounter = new Map<number, GalleryApiPhoto[]>();
  withEmr.forEach((row, index) => {
    const error = errorMessageOf(galleries[index]);
    photosByEncounter.set(row.encounterId, error === null ? galleryFromApi(dataOf(galleries[index])) : []);
  });

  writeSlice(controller, params, {
    ids,
    galleryHistory: galleryHistoryGroupsOf(encounters, photosByEncounter),
    error: null,
  });
};

/**
 * ข้อมูลของ Patient Panel ที่ไม่ผูกกับเวชระเบียน
 *   GET  /apis/CRM/loyalty-account/?patient=   บัญชีสมาชิก
 *   GET  /apis/CRM/loyalty-tier/                ระดับสมาชิกทั้งหมด (ลำดับ -> สี)
 *   GET  /apis/QUE/patient-appointment/v2/?patient_id=   นัดหมายของผู้ป่วย (estimated_at = เวลานัด)
 *   GET  /apis/core/encounter/{encounter}/       ยาที่ใช้ประจำ / สิทธิ
 *   Patient latest-channel / latest-special-request ผ่าน patientTeamMessagesV4
 */
const LoadPanel: Handler<Ctx<"LOAD_PANEL">> = async (controller, params) => {
  const { ids } = params;
  if (!ids.patientId)
    return writeSlice(controller, params, { error: "ยังไม่มีผู้ป่วย (patient id)" });

  const [encounterResult, appointmentResult, messages] = await Promise.all([
    ids.encounterId
      ? EncounterDetail.retrieve({
          pk: ids.encounterId,
          apiToken: controller.apiToken,
          extra: extraOf(controller),
        })
      : Promise.resolve([null, null, null] as ApiTuple),
    PatientAppointmentV2List.list({
      params: {
        patient_id: ids.patientId,
        status: OPEN_APPOINTMENT_STATUS,
        ordering: "estimated_at,id",
        limit: 50,
      },
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    }),
    loadPatientTeamMessages(ids.patientId, { apiToken: controller.apiToken, extra: extraOf(controller) })
      .catch(() => ({ latestChannel: undefined, specialRequests: undefined,
        errors: ["โหลดข้อความถึงทีมล่าสุดไม่สำเร็จ กรุณาลองเปิดผู้รับบริการใหม่"] })),
  ]);
  const encounter = asDict(dataOf(encounterResult));

  /* สมาชิกแยกตามหน่วย: ใช้หน่วยของ encounter ไม่ใช่หน่วยที่ login */
  const loyaltyExtra = {
    ...extraOf(controller),
    division: asNumber(encounter.division) ?? controller.data?.division,
  };
  const [accountResult, tierResult] = await Promise.all([
    LoyaltyAccountListView.list({
      params: { patient: ids.patientId },
      apiToken: controller.apiToken,
      extra: loyaltyExtra,
    }),
    LoyaltyTierListView.list({
      params: {},
      apiToken: controller.apiToken,
      extra: loyaltyExtra,
    }),
  ]);

  /* ผลของ encounter ก่อนหน้ามาถึงช้า -> ไม่เขียนทับผู้ป่วยคนใหม่ */
  if (sliceOf(controller).ids.patientId !== ids.patientId) return;

  const today = moment();
  writeSlice(controller, params, {
    member: memberFromApi(dataOf(accountResult), dataOf(tierResult), ids.patientId, today),
    nextAppointment: nextAppointmentFromApi(dataOf(appointmentResult), today),
    channels: termNamesOf(messages.latestChannel?.items),
    latestChannel: messages.latestChannel,
    specialRequests: messages.specialRequests,
    teamMessageErrors: messages.errors,
    medications: termNamesOf(encounter.regularly_taken_supplement_data_list),
    coverage: asText(encounter.coverage),
    panelLoaded: true,
    checkout: ids.encounterId ? checkoutEncounterOf(encounter) : null,
    error: errorMessageOf(accountResult) ?? errorMessageOf(appointmentResult),
  });
};

const LoadNurse: Handler<Ctx<"LOAD_NURSE">> = async (controller, params) => {
  const { ids } = params;
  if (!ids.encounterId) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกผู้ป่วย (encounter)" });

  const [notesResult, latestResult, templatesResult] = await Promise.all([
    NurseNoteItemList.list({
      params: { encounter: ids.encounterId, sort_descending: true },
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    }),
    NurseNoteLatest.retrieve({
      params: { encounter: ids.encounterId },
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    }),
    NurseNoteTemplateItemList.list({
      params: { division: controller.data?.division },
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    }),
  ]);

  if (isStaleEncounter(controller, ids)) return undefined;

  const error = errorMessageOf(notesResult);

  writeSlice(controller, params, {
    ids,
    nurseNotes: error ? [] : nurseNotesFromApi(dataOf(notesResult)),
    nurseNoteHeadId: nurseNoteHeadIdFromApi(dataOf(latestResult)),
    nurseTemplates: nurseTemplatesFromApi(dataOf(templatesResult)),
    /* Focus ที่เลือกได้จาก dropdown (86636) ใช้แถวเดียวกันกับ template item */
    nurseFocusTemplates: nurseFocusTemplatesFromApi(dataOf(templatesResult)),
    error,
  });
};

/* ============================================================== */
/*   SECTION 6 · WRITE                                             */
/* ============================================================== */

/**
 * save ประวัติ + ตรวจร่างกาย (patient_screen)
 * new vs update ตัดสินจาก patient_illness.id เหมือน V3 (CardPatientInfo.tsx:292)
 * คืนข้อความ error หรือ null (persist* ทุกตัวไม่เขียน state ให้ผู้เรียกรวมผล)
 */
const persistExam = async (controller: Ctl, params: Ctx<"SAVE_RECORD">): Promise<string | null> => {
  const slice = sliceOf(controller);
  const { ids } = params;

  const payload = buildPatientScreenPayload({
    ids,
    subjective: params.subjective ?? slice.subjective,
    rows: [
      ...(params.rows ?? slice.examRows),
      ...(params.templateRows ?? selectedTemplateRows(slice.templateRows, slice.selectedTemplateIds)),
    ],
    other: params.other ?? slice.other,
  });

  const result =
    slice.subjective.illnessId === null
      ? await PatientEMRNew.post({ data: payload, apiToken: controller.apiToken })
      : await PatientEMREdit.put({ data: payload, apiToken: controller.apiToken });

  return errorMessageOf(result);
};

/** PUT /apis/DPO/patient_history/emr/{emr}/ (DPOI.ts:788-796) */
const persistHistory = async (
  controller: Ctl,
  params: { ids: ExamIds; subjective?: Subjective },
): Promise<string | null> => {
  const { ids } = params;
  const subjective = params.subjective ?? sliceOf(controller).subjective;

  if (!hasHistoryValues(subjective)) return null;

  const result = await PatientHistoryDetail.update({
    emr: ids.emrId as number,
    data: buildPatientHistoryPayload({ emrId: ids.emrId as number, subjective }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  return errorMessageOf(result);
};

/**
 * V3: ถ้าผู้ใช้เป็นนักศึกษา ต้องผ่าน modal ยืนยันการตรวจสอบก่อน save
 * (approved_by_username/approved_by_password ถูกส่งเข้า PUT diagnosis/)
 * บันทึกร่างไม่ต้องอนุมัติ: BE เก็บเป็นยังไม่อนุมัติ เหมือนปุ่ม Save ของนักศึกษาใน V3
 */
const persistDiagnosis = async (controller: Ctl, params: Ctx<"SAVE_RECORD">): Promise<string | null> => {
  const { ids } = params;
  const slice = sliceOf(controller);
  const diagnosis = params.diagnosis ?? slice.diagnosis;

  if (!diagnosis.principal || diagnosis.principal.icd10 === null) {
    return "กรุณาระบุ Principal Diagnosis ให้ถูกต้อง";
  }

  const mustApprove =
    params.mode === "confirm" && needsSupervisorApproval(slice.diagnosisApproval, params.currentUserId);
  const username = `${params.approvedByUsername ?? ""}`.trim();
  const password = `${params.approvedByPassword ?? ""}`;
  if (mustApprove && (username === "" || password === "")) {
    return "บันทึกนี้ต้องมีการอนุมัติ: ต้องใส่ชื่อผู้ใช้และรหัสผ่านของแพทย์ผู้ดูแล";
  }

  /* buildDiagnosisPayload ให้ field name ตาม DPOI.ts:181-192
     SDK รับ data เป็น Partial<DiagnosisMedicalRecordSerializer> จึง cast ณ จุดเรียกจุดเดียว */
  const result = await DiagnosisMedicalRecordDetail.update({
    pk: ids.emrId as number,
    data: buildDiagnosisPayload(diagnosis, ids.emrId, { username, password }) as never,
    apiToken: controller.apiToken,
  });

  return errorMessageOf(result);
};

/**
 * บันทึกร่าง / Confirm ของหน้าตรวจรักษา
 * ทำทีละส่วนตามลำดับ (ถ้ายิงพร้อมกัน ส่วนที่สำเร็จจะล้าง error ของส่วนที่ล้ม และ reload แข่งกันเขียน slice)
 * ส่วนหนึ่งล้มยังบันทึกส่วนอื่นต่อ เพื่อไม่ให้ข้อมูลที่ถูกต้องหาย แล้วอ่านกลับก่อนรายงานผล
 * Confirm สำเร็จเมื่อทุกส่วนผ่าน และอ่านกลับแล้วมี Principal จริง
 */
const SaveRecord: Handler<Ctx<"SAVE_RECORD">> = async (controller, params) => {
  const { ids } = params;
  const mode: RecordSaveMode = params.mode ?? "draft";
  const requestId = params.requestId ?? "";

  if (!ids.emrId) {
    const error = "ยังไม่มีเวชระเบียน ไม่สามารถบันทึกได้";
    return writeSlice(controller, params, {
      ids,
      error,
      recordSave: { requestId, mode, ok: false, failed: [{ section: "exam", message: error }] },
    });
  }

  writeSlice(controller, params, { ids, saving: true, error: null });

  const failed: RecordSaveFailure[] = [];
  let savedAny = false;

  const attempt = async (
    section: RecordSaveFailure["section"],
    label: string,
    task: () => Promise<string | null>,
  ): Promise<void> => {
    const error = await task();
    if (error === null) savedAny = true;
    else failed.push({ section, message: `บันทึก${label}ไม่สำเร็จ: ${error}` });
  };

  await attempt("exam", "ประวัติและตรวจร่างกาย", () => persistExam(controller, params));
  await attempt("history", "ประวัติในอดีต", () => persistHistory(controller, params));

  /* V3: บันทึก Present illness แล้วการ์ดอ่านเวชระเบียนซ้ำแบบ auto_checkin default
     -> IN_QUEUE กลายเป็น IN_EXAM เอง (CardPatientInfo.tsx:525-528)
     V4 ให้ LoadExam (ซึ่งวิ่งหลัง save เสมอ) เป็นตัวขยับ ผ่าน checkInExamIfNeeded
     จึงไม่ขยับสถานะเมื่อบันทึก exam ล้ม (กันสถานะวิ่งทั้งที่ข้อมูลไม่อยู่บน server) */

  /* ร่างที่ยังไม่เลือก Principal ไม่มีวินิจฉัยให้บันทึก ส่วน Confirm ต้องมีเสมอ */
  const principal = (params.diagnosis ?? sliceOf(controller).diagnosis).principal;
  if (mode === "confirm" || (principal && principal.icd10 !== null)) {
    await attempt("diagnosis", "วินิจฉัย", () => persistDiagnosis(controller, { ...params, mode }));
  }

  /* อ่านกลับใหม่ + ให้ LoadExam ตัดสิน auto check-in (V3: save แล้วสถานะเปลี่ยนเอง)
     บันทึก exam ล้ม = ห้ามขยับสถานะ เพราะข้อมูลยังไม่อยู่บน server */
  await LoadExam(controller, {
    action: ACTIONS.LOAD,
    card: params.card,
    ids,
    currentStatus: params.currentStatus,
    skipCheckIn: failed.some((item) => item.section === "exam"),
  });
  if (isStaleEncounter(controller, ids)) return undefined;

  const reloadError = errorTextOf(controller.getState().errorMessage?.[params.card ?? CARD_NAME]);
  if (reloadError !== null) failed.push({ section: "reload", message: reloadError });
  else if (mode === "confirm" && failed.length === 0 && sliceOf(controller).diagnosis.principal === null) {
    failed.push({ section: "diagnosis", message: "อ่านกลับแล้วไม่พบ Principal Diagnosis" });
  }

  const at = new Date().toISOString();

  return writeSlice(controller, params, {
    ids,
    saving: false,
    error: failed.length > 0 ? failed.map((item) => item.message).join(" · ") : null,
    recordSave: { requestId, mode, ok: failed.length === 0, failed },
    ...(savedAny ? { savedAt: at, recordSavedAt: at } : {}),
  });
};

/**
 * เรียกผู้ป่วยเข้าตรวจ — ทำตาม V3 "IN_QUEUE:IN_EXAM" ให้ครบ 2 จังหวะ
 *   1) PUT patient_screen assigns doctor ให้ patient_illness + patient_exam_other
 *      (CardPatientListInterface.ts:1905-1910)
 *   2) PUT encounter-opd-check-in/{encounter}/ ย้าย IN_QUEUE -> IN_EXAM
 *
 * สถานะอื่นของขั้นรอพบแพทย์ (SCREENED, RECEIVE_RESULT) และ checkout แล้ว
 * ต้อง EncounterActionOnly(OPD_QUEUE_EXAM) เข้า IN_QUEUE ก่อน check-in
 * (CardPatientListInterface.ts:1919-1929 ทำลำดับนี้ตอน CANCEL_CHECKOUT)
 */
const StartExam: Handler<
  Ctx<"START_EXAM">
> = async (controller, params) => {
  /* กดจากคิวครั้งแรกยังไม่มี emr ใน params -> หาเองจาก encounter ไม่ใช้ของที่ค้างใน state */
  const resolved = await resolveEmrId(controller, { ids: params.ids });
  const ids = resolved.ids;

  if (resolved.error || !ids.emrId || !ids.encounterId) {
    return writeSlice(controller, params, {
      ids,
      saving: false,
      error: resolved.error ?? "ยังไม่มีเวชระเบียน/encounter ไม่สามารถเปิดตรวจได้",
    });
  }

  const state = controller.getState() as {
    selectedEncounter?: { id?: unknown; status?: string; approve_by?: number; doctor?: number };
    selectedEmr?: { doctor?: number; encounter?: unknown };
  };
  /* state กลางอาจยังเป็นผู้ป่วยคนก่อน ใช้เฉพาะเมื่อเป็น encounter เดียวกัน */
  const loadedEncounter =
    numberOrUndefined(state.selectedEncounter?.id) === ids.encounterId ? state.selectedEncounter : undefined;
  const loadedEmr = emrOfEncounter(state.selectedEmr, { id: ids.encounterId });
  const currentStatus = params.currentStatus ?? loadedEncounter?.status ?? "";

  /** เกิดปัญหาระหว่างเรียกเข้าตรวจ ยังต้องโหลดเวชระเบียนให้หน้าใช้งานต่อได้ แล้วค่อยแจ้ง error */
  const finishWithError = async (message: string): Promise<void> => {
    await LoadExam(controller, { action: ACTIONS.LOAD, card: params.card, ids, skipCheckIn: true });
    writeSlice(controller, params, { ids, saving: false, error: message });
  };

  /* seed ต้องมี doctor id (V3: approve_by || doctor ของ encounter) ไม่งั้นผู้ป่วยไม่ถูกย้ายเข้า IN_EXAM
     ถ้า caller ยังไม่มี (เช่นกดจากคิว ก่อนที่ parent จะโหลด encounter detail) -> อ่าน encounter เอง
     แบบเดียวกับ V3 CardPatientListInterface.ts:862-870 */
  let doctorId = ids.doctorId ?? doctorIdOf(loadedEncounter, loadedEmr);
  if (doctorId === null) {
    const detail = await EncounterDetail.retrieve({
      pk: ids.encounterId,
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    });
    doctorId = doctorIdOf(asDict(dataOf(detail)));
  }
  if (doctorId === null) {
    return finishWithError("ไม่พบ id แพทย์ผู้ตรวจบน encounter ไม่สามารถเรียกเข้าตรวจได้");
  }
  const idsWithDoctor: ExamIds = { ...ids, doctorId };

  writeSlice(controller, params, { ids, saving: true, error: null });

  const seed = buildExamStartPayload(idsWithDoctor);
  const saved = await PatientEMREdit.put({ data: seed, apiToken: controller.apiToken });
  const saveError = errorMessageOf(saved);

  /* encounter-opd-check-in ย้ายได้เฉพาะ IN_QUEUE: สถานะอื่นของขั้นรอพบแพทย์และ checkout แล้ว
     ต้อง OPD_QUEUE_EXAM เข้า IN_QUEUE ก่อน */
  let statusError: string | null = null;
  if (isCheckedOutStatus(currentStatus) || (isCallInStatus(currentStatus) && currentStatus !== "IN_QUEUE")) {
    const status = await EncounterActionOnly.patch({
      pk: ids.encounterId,
      data: { action: ACTION_START_EXAM },
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    });
    statusError = errorMessageOf(status);
  }

  /* auto check-in: V3 ให้อ่านเวชระเบียนแล้ว backend เปลี่ยนสถานะเอง
     V4 เรียก endpoint เดียวกันตรง ๆ (force = ผู้ใช้กดปุ่มเรียกเข้าตรวจเอง card gate บทบาทไว้แล้ว) */
  const checkIn = await checkInExamIfNeeded(controller, {
    ids: idsWithDoctor,
    currentStatus,
    force: true,
  });
  const checkError = checkIn.error;

  const startError = saveError ?? statusError ?? checkError;
  if (startError) return finishWithError(startError);

  /* ให้ shared state (selectedEncounter / patient panel / queue) ตรงกับสถานะใหม่ */
  controller.handleEvent({ message: "HandleRefreshEncounter", params: { encounterId: ids.encounterId } });

  await LoadExam(controller, {
    action: ACTIONS.LOAD,
    card: params.card,
    ids: idsWithDoctor,
    currentStatus,
    skipCheckIn: true,
  });
  writeSlice(controller, params, { ids: idsWithDoctor, saving: false, savedAt: new Date().toISOString(), error: null });
};

/**
 * past / personal / family history save แยกจาก patient_screen
 * PUT /apis/DPO/patient_history/emr/{emr}/ (DPOI.ts:788-796)
 */
const SaveHistory: Handler<Ctx<"SAVE_HISTORY">> = async (
  controller,
  params,
) => {
  const ids = params.ids;
  const subjective = params.subjective ?? sliceOf(controller).subjective;

  if (!ids.emrId) return writeSlice(controller, params, { ids, error: "ยังไม่มีเวชระเบียน ไม่สามารถบันทึกประวัติได้" });
  if (!hasHistoryValues(subjective)) return writeSlice(controller, params, { ids, error: null });

  writeSlice(controller, params, { ids, saving: true, error: null });

  const error = await persistHistory(controller, { ids, subjective });
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadExam(controller, { action: ACTIONS.LOAD, card: params.card, ids });
  const at = new Date().toISOString();
  writeSlice(controller, params, { ids, saving: false, savedAt: at, recordSavedAt: at, error: null });
};

/**
 * GET /apis/DPO/sensitive_note/emr/{emr}/
 * V3: getSensitiveNoteEMR (DPOI.ts:636-642) — 404 แปลว่ายังไม่มี record (V4 เก็บเป็นค่าว่าง ไม่ถือว่าเป็น error)
 */
const LoadSensitiveNote: Handler<Ctx<"LOAD_SENSITIVE_NOTE">> = async (controller, params) => {
  const ids = params.ids;
  if (!ids.emrId) return writeSlice(controller, params, { ids, error: "ยังไม่มีเวชระเบียนสำหรับอ่านบันทึกเฉพาะ" });

  const result = await ClassifiedPatientHistoryDetail.retrieve({
    pk: ids.emrId as number,
    params: {},
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  const notFound = error !== null && /not found|ไม่พบ|404/i.test(error);

  writeSlice(controller, params, {
    ids,
    sensitiveNoteRecord: sensitiveNoteRecordFromApi(dataOf(result)),
    error: notFound ? null : error,
  });
};

/**
 * V3 (SubPatientHistory.tsx:185-210): record มี id -> PUT ทั้ง record / ยังไม่มี -> POST สร้างใหม่
 * V4: record มี id -> PATCH เฉพาะ sensitive_note (verify จาก staging: PATCH {sensitive_note} -> 200)
 *     ยังไม่มี record -> PUT พร้อม emr + division (ขาด division -> 400 {"division":["ระบุข้อมูลในช่องนี้"]})
 */
const SaveSensitiveNote: Handler<Ctx<"SAVE_SENSITIVE_NOTE">> = async (controller, params) => {
  const ids = params.ids;
  const slice = sliceOf(controller);
  const note = params.sensitiveNote ?? slice.subjective.sensitiveNote;
  const emrId = ids.emrId;

  if (!emrId) return writeSlice(controller, params, { ids, error: "ยังไม่มีเวชระเบียนสำหรับบันทึกข้อมูลเฉพาะ (sensitive note)" });

  const record = slice.sensitiveNoteRecord;

  if (record.id === undefined) {
    const divisionId = controller.data?.division ?? record.divisionIds[0];
    if (divisionId === undefined) {
      return writeSlice(controller, params, {
        ids,
        error: "ยังไม่ได้เลือกแผนก (division) — backend ต้อง division ตอนสร้าง recordใหม่",
      });
    }

    writeSlice(controller, params, { ids, saving: true, error: null });

    const result = await ClassifiedPatientHistoryDetail.update({
      pk: emrId as number,
      params: {},
      data: buildSensitiveNoteCreatePayload({ emrId: emrId as number, divisionId, note }) as never,
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    });
    const error = errorMessageOf(result);
    if (error) return writeSlice(controller, params, { ids, saving: false, error });

    await LoadSensitiveNote(controller, { ...params, action: ACTIONS.LOAD_SENSITIVE_NOTE });
    return writeSlice(controller, params, {
      ids,
      saving: false,
      savedAt: new Date().toISOString(),
      recordSavedAt: new Date().toISOString(),
      /* อ่าน slice ล่าสุด: SAVE_HISTORY วิ่งพร้อมกันและอาจ reload ไปแล้ว ห้ามเขียนค่าเก่าทับ */
      subjective: { ...sliceOf(controller).subjective, sensitiveNote: note },
      error: null,
    });
  }

  writeSlice(controller, params, { ids, saving: true, error: null });

  const result = await ClassifiedPatientHistoryDetail.patch({
    pk: emrId as number,
    params: {},
    data: buildSensitiveNoteUpdatePayload({ note }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  /* อ่าน record กลับเพื่อเอา edit_user / เวลาของ backend มาแสดง (V3 PHV แสดง 2 ค่านี้) */
  await LoadSensitiveNote(controller, { ...params, action: ACTIONS.LOAD_SENSITIVE_NOTE });

  return writeSlice(controller, params, {
    ids,
    saving: false,
    savedAt: new Date().toISOString(),
    recordSavedAt: new Date().toISOString(),
    subjective: { ...sliceOf(controller).subjective, sensitiveNote: note },
    error: null,
  });
};

const SearchIcd10: Handler<Ctx<"SEARCH_ICD10">> = async (
  controller,
  params,
) => {
  const search = `${params.search ?? ""}`.trim();

  const method: IcdSearchMethod = params.method ?? "medterm";

  /* V3 ใช้ขั้นต่ำ 3 ตัวอักษร (TreatmentResult.ts:432-507) แต่ 86627 ข้อ 1.1 ให้ค้น "รหัส" แบบบางส่วนได้
     เช่น พิมพ์ "A1" หรือ "M5" ต้องมีผลขึ้นมา -> รหัสใช้ขั้นต่ำ 2 ตัวอักษร, ชื่อโรคคง 3 ตัวอักษร */
  const minLength = method === "icdcode" ? 2 : 3;
  if (search.length < minLength) return writeSlice(controller, params, { icdResults: [], icdQuery: search });

  const result = await icd10KeyUp.post({
    method,
    data: { [method]: search },
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);

  writeSlice(controller, params, {
    icdQuery: search,
    icdResults: error ? [] : icdHitsFromSearch(dataOf(result)),
    error,
  });
};

/* ---- gallery write ---- */

const UploadPhoto: Handler<Ctx<"UPLOAD_PHOTO">> = async (
  controller,
  params,
) => {
  const ids = params.ids;
  if (!ids.emrId) return writeSlice(controller, params, { ids, error: "ยังไม่มีเวชระเบียน ไม่สามารถอัปโหลดภาพได้" });

  const emr = ids.emrId as number;
  /*
   * POST /apis/DPO/medical_record/gallery/{emr}/
   * body = emr + image (data:image/png;base64,...) + description
   * field `items` ที่ V3 ส่ง (CardImageGallery.tsx:373-387) API ไม่ได้ใช้ — ส่งแล้วผลเหมือนกัน จึงไม่ส่งใน V4
   * ถ้า image ไม่ใช่ file/data URI ที่ decode ได้จริง จะได้ 400 {"image":["ไม่ได้รับไฟล์"]}
   */
  const result = await MedicalRecordGalleryList.create({
    emr,
    data: {
      emr,
      image: params.image,
      description: params.description ?? "",
    } as never,
    apiToken: controller.apiToken,
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadGallery(controller, { action: ACTIONS.LOAD_GALLERY, card: params.card, ids });
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), error: null });
};

const UpdatePhoto: Handler<Ctx<"UPDATE_PHOTO">> = async (
  controller,
  params,
) => {
  const ids = params.ids;

  /*
   * ใช้ PATCH ไม่ใช่ PUT (ต่างจาก V3 ที่ putMedicalRecordPicture ใช้ PUT:
   * src/HIS/Interface/DPOI.ts:510-524)
   *
   * verify จาก staging API แล้ว:
   *   PUT /apis/DPO/medical_record/picture/{pk}/  body {emr, description}
   *     -> 400 {"image": ["ไม่ได้รับไฟล์"]}      (PUT ต้องส่ง image เป็นไฟล์ทุกครั้ง)
   *   PUT ... body {image: "https://storage.googleapis.com/..."}
   *     -> 400 {"image": ["ไม่รองรับรูปภาพที่อัพโหลด ..."]} (URL ที่ API ตอบกลับเอาก่อนส่งกลับไม่ได้)
   *   PATCH body {description} / {image:dataURI} / {is_active:false} -> 200
   *
   * image ที่รับได้ต้องเป็น data URI base64 ที่ decode ได้ และเป็นรูปจริง (png/jpeg)
   * svg data URI (ทั้งแบบ percent-encoded และ base64) ถูกปฏิเสธทั้งหมด
   */
  const payload = buildPictureUpdatePayload({
    image: params.image,
    description: params.description,
  });
  if (Object.keys(payload).length === 0) {
    return writeSlice(controller, params, { ids, saving: false, error: "ไม่มีข้อมูลที่ต้องบันทึก" });
  }

  const result = await MedicalRecordPictureDetail.patch({
    pk: params.photoId,
    data: payload as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadGallery(controller, { action: ACTIONS.LOAD_GALLERY, card: params.card, ids });
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), error: null });
};

/**
 * 86630 · บันทึกเส้นที่วาดเป็น "layer" ของภาพ (Redmine #86780)
 *   POST /apis/DPO/clinical-picture-annotation/  body {picture, items:[...]}  (batch)
 *   points เป็นค่าสัมพัทธ์ 0..1 (card แปลงจาก px ของ canvas ให้)
 * backend ไม่แตะไฟล์ ClinicalPicture.image จึง redraw ได้ทุกขนาด และย้อนกลับได้ทีละชั้น
 */
const SavePhotoAnnotations: Handler<
  Ctx<"SAVE_PHOTO_ANNOTATIONS">
> = async (controller, params) => {
  const ids = params.ids;
  const items = params.items ?? [];
  if (!params.photoId) {
    return writeSlice(controller, params, { ids, saving: false, error: "ไม่พบ id ภาพที่ต้องการบันทึกเส้น" });
  }
  if (items.length === 0) {
    return writeSlice(controller, params, { ids, saving: false, error: "ไม่มีเส้นที่วาดต้องบันทึก" });
  }

  const result = await ClinicalPictureAnnotationList.create({
    data: { picture: params.photoId, items } as never,
    params: {},
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error !== null) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadGallery(controller, { action: ACTIONS.LOAD_GALLERY, card: params.card, ids });
  writeSlice(controller, params, {
    ids,
    saving: false,
    savedAt: new Date().toISOString(),
    gallerySavedMessage: `บันทึกเส้นบนภาพ ${items.length} ชั้นแล้ว`,
    error: null,
  });
};

/**
 * ย้อนกลับ / ล้างเส้น (86630 ข้อ 3): DELETE /apis/DPO/clinical-picture-annotation/<id>/ ทีละชั้น
 * backend เป็น soft delete (active=False) จึงไม่กระทบภาพต้นฉบับและอ่านกลับได้ด้วย
 */
const DeletePhotoAnnotations: Handler<
  Ctx<"DELETE_PHOTO_ANNOTATIONS">
> = async (controller, params) => {
  const ids = params.ids;
  const annotationIds = params.annotationIds ?? [];
  if (annotationIds.length === 0) {
    return writeSlice(controller, params, { ids, saving: false, error: "ไม่พบชั้นเส้นที่ต้องลบ" });
  }

  const results = await Promise.all(
    annotationIds.map((pk) =>
      ClinicalPictureAnnotationDetail.delete({
        pk,
        params: {},
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
  );
  const failed = results.map(errorMessageOf).find((item) => item !== null);
  if (failed !== null && failed !== undefined) {
    return writeSlice(controller, params, { ids, saving: false, error: failed });
  }

  await LoadGallery(controller, { action: ACTIONS.LOAD_GALLERY, card: params.card, ids });
  writeSlice(controller, params, {
    ids,
    saving: false,
    gallerySavedMessage: `ลบชั้นเส้นที่วาดแล้ว ${annotationIds.length} ชั้น`,
    error: null,
  });
};

/**
 * ลบแบบ soft: PATCH {is_active: false}
 * - V3 ใช้ PUT พร้อม image เดียวกัน (setUpController.ts:511-514) ซึ่ง 400 ใน V4
 *   เพราะ PUT ต้องได้ไฟล์ image จริง และ URL ที่ API ตอบกลับส่งกลับไปแล้วถูกปฏิเสธ
 * - verify จาก staging API: PATCH {is_active:false} -> 200 และ gallery list
 *   ไม่คืนค่านั้นอีก (total ลดลง) ส่วน DELETE -> 204 (hard delete) ซึ่ง V4 ไม่ใช้
 */
const DeletePhoto: Handler<Ctx<"DELETE_PHOTO">> = async (
  controller,
  params,
) => {
  const ids = params.ids;
  if (!params.photoId) {
    return writeSlice(controller, params, { ids, saving: false, error: "ไม่พบ id ภาพที่ต้องการลบ" });
  }

  const result = await MedicalRecordPictureDetail.patch({
    pk: params.photoId,
    data: buildPictureUpdatePayload({ active: false }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadGallery(controller, { action: ACTIONS.LOAD_GALLERY, card: params.card, ids });
  writeSlice(controller, params, { ids, saving: false, error: null });
};

/**
 * PATCH /apis/DPO/medical_record/picture/{pk}/ body {"is_active": true}
 * V3 เก็บ undoState แล้วเรียก updateImageDetail(undoState) (CardImageGallery.tsx:171-179,363-371)
 * ที่นี่เก็บ id ของภาพที่ลบไปแล้วไว้ใน card แล้วส่ง is_active=true คืน (verify กับ staging แล้ว: PATCH {is_active:false} -> 200 แล้ว list ไม่คืนรายการนั้น)
 */
const RestorePhoto: Handler<Ctx<"RESTORE_PHOTO">> = async (controller, params) => {
  const ids = params.ids;
  if (!params.photoId) {
    return writeSlice(controller, params, { ids, saving: false, error: "ไม่พบ id ภาพที่ต้องย้อนกลับ" });
  }

  const result = await MedicalRecordPictureDetail.patch({
    pk: params.photoId,
    data: buildPictureUpdatePayload({ active: true }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadGallery(controller, { action: ACTIONS.LOAD_GALLERY, card: params.card, ids });
  writeSlice(controller, params, { ids, saving: false, error: null });
};

/**
 * GET /apis/DPO/template-gallery/ (V3 `getTemplateGalleryImage`: DPOI.ts:526-531)
 * backend ตอบ { items: [{id, name, image, active}] } — staging ตอนนี้ไม่มีข้อมูล (total 0)
 * แถวที่ไม่มี image ถูกตัดออกเพราะ UI ไม่มีประโยชน์
 */
const LoadGalleryTemplates: Handler<Ctx<"LOAD_GALLERY_TEMPLATES">> = async (controller, params) => {
  const ids = params.ids;

  const result = await TemplateGalleryList.list({
    params: {},
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);

  writeSlice(controller, params, {
    ids,
    galleryTemplates: error ? [] : galleryTemplatesFromApi(dataOf(result)),
    error,
  });
};

/* ---- nurse note write ---- */

/**
 * nurse-note-item ทุก write ต้องระบุผู้บันทึก (verify จาก staging API ด้วย curl แล้ว)
 *   ไม่มี username/password และไม่มี user_token -> 400 {"username":["ระบุข้อมูลในช่องนี้"],"password":[...]}
 *   user_token ไม่ valid                    -> 400 ["invalid user token"]
 *   username/password ไม่ถูกต้อง           -> 400 {"__all__":["กรุณาใส่  ชื่อผู้ใช้  และรหัสผ่านที่ถูกต้อง ..."]}
 * HISV3 ส่ง user_token จาก state.django.user.token (PTMInterface.ts:515-535)
 * ถ้าวงงานไม่มี token ต้องส่ง username/password แบบ V3 เดิม (PTMI.ts:781-900) ที่ Multidisciplinary ใช้
 */
type NurseAuth = { username?: string; password?: string; user_token?: string };

const NURSE_AUTH_MISSING =
  "กรุณาใส่  ชื่อผู้ใช้  และรหัสผ่าน (backend ไม่รับบันทึกการพยาบาลโดยไม่ยืนยันผู้บันทึก)";

const nurseAuthOf = (
  controller: Ctl,
  params: { username?: string; password?: string },
): NurseAuth | null => {
  const app = controller.getState() as { django?: { user?: { token?: string } } };
  const username = `${params.username ?? ""}`.trim();
  const password = `${params.password ?? ""}`;

  if (username !== "" && password !== "") return { username, password };

  const token = app.django?.user?.token;
  if (token) return { user_token: token };

  return null;
};

const SaveNurseNote: Handler<
  Ctx<"SAVE_NURSE_NOTE">
> = async (controller, params) => {
  const ids = params.ids;
  const values = params.values ?? {};
  const filled = Object.values(values).some((value) => `${value ?? ""}`.trim() !== "");

  if (!ids.encounterId) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกผู้ป่วย" });
  if (!filled) return writeSlice(controller, params, { ids, error: "กรุณากรอกข้อความก่อนบันทึก" });

  const auth = nurseAuthOf(controller, params);
  if (auth === null) return writeSlice(controller, params, { ids, error: NURSE_AUTH_MISSING });

  const result = await NurseNoteItemList.create({
    data: buildNurseNotePayload({
      encounterId: ids.encounterId,
      nurseNoteId: sliceOf(controller).nurseNoteHeadId,
      division: controller.data?.division,
      date: params.date ?? "",
      values,
      auth,
      workingShift: params.workingShift,
      doctorOrderId: params.doctorOrderId,
    }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadNurse(controller, { action: ACTIONS.LOAD_NURSE, card: params.card, ids });
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), nurseSavedMessage: "เพิ่มบันทึกแล้ว", error: null });
};

/**
 * 86637 · สร้าง Focus ใหม่ = POST /apis/PTM/nurse-note-template/
 *        (backend สร้าง NurseNoteTemplate + bind division ให้ในคำสั่งเดียว - Redmine #86781)
 *        แก้ Focus ที่มีอยู่ = PATCH /apis/PTM/nurse-note-template-item/<id>/
 * แล้วอ่าน Focus list กลับผ่าน LoadNurse เพื่อให้ dropdown ใช้ได้ทันที
 */
const SaveNurseTemplate: Handler<
  Ctx<"SAVE_NURSE_TEMPLATE">
> = async (controller, params) => {
  const ids = params.ids;
  const draft = params.draft ?? emptyNurseTemplateDraft();
  const invalid = validateNurseTemplateDraft(draft);
  if (invalid !== null) return writeSlice(controller, params, { ids, nurseTemplateError: invalid });

  const focusName = `${draft.name}`.trim();
  const result =
    draft.itemId === null
      ? await NurseNoteTemplateList.create({
          data: nurseTemplateCreatePayload(draft, controller.data?.division) as never,
          apiToken: controller.apiToken,
          extra: extraOf(controller),
        })
      : await NurseNoteTemplateItemDetail.patch({
          pk: draft.itemId,
          data: nurseTemplateUpdatePayload(draft) as never,
          apiToken: controller.apiToken,
          extra: extraOf(controller),
        });

  const error = errorMessageOf(result);
  if (error !== null) {
    /* ส่ง body ดิบเข้า friendlyNurseTemplateErrorText ก่อน เพราะ nested serializer ตอบ
       {"items":[{"focus":["..."]}]} ซึ่ง errorTextOf ทั่วไป flatten ได้ไม่สวย ("items: [object Object]") */
    /* 86637: error ของ template แสดงในกล่องตั้งค่า template เท่านั้น (nurseTemplateError)
       ไม่เขียนลง error รวมของการ์ด เพราะ bar เดิมขึ้น prefix "อ่านบันทึกการพยาบาลไม่สำเร็จ" ทำให้อ่านผิดความหมาย */
    const mapped = friendlyNurseTemplateErrorText(result[1] ?? error);
    return writeSlice(controller, params, { ids, saving: false, nurseTemplateError: mapped });
  }

  await LoadNurse(controller, { action: ACTIONS.LOAD_NURSE, card: params.card, ids });
  writeSlice(controller, params, {
    ids,
    saving: false,
    nurseSavedMessage: nurseTemplateSavedMessage(focusName),
    nurseTemplateError: "",
    error: null,
  });
};

/**
 * 86637 · DELETE /apis/PTM/nurse-note-template-item/<id>/
 * backend เป็น soft delete (active=False) จึงไม่กระทบ NurseNote ที่บันทึกไปแล้ว
 * (NurseNoteItem เก็บ focus/A/I/E เป็นข้อความของตัวเอง ไม่ได้ FK ถึง template)
 */
const DeleteNurseTemplate: Handler<
  Ctx<"DELETE_NURSE_TEMPLATE">
> = async (controller, params) => {
  const ids = params.ids;
  const itemId = params.itemId;
  if (!itemId) return writeSlice(controller, params, { ids, nurseTemplateError: "ไม่ได้เลือก Focus ที่ต้องการลบ" });

  const result = await NurseNoteTemplateItemDetail.delete({
    pk: itemId,
    params: {},
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error !== null) {
    const mapped = friendlyNurseTemplateErrorText(result[1] ?? error, "ลบ template ไม่สำเร็จ");
    return writeSlice(controller, params, { ids, saving: false, nurseTemplateError: mapped });
  }

  await LoadNurse(controller, { action: ACTIONS.LOAD_NURSE, card: params.card, ids });
  writeSlice(controller, params, {
    ids,
    saving: false,
    nurseSavedMessage: nurseTemplateDeletedMessage(`${params.name ?? ""}`.trim() || "Focus"),
    nurseTemplateError: "",
    error: null,
  });
};

/**
 * PATCH /apis/PTM/nurse-note-item/{pk}/
 * V3 ใช้ putNurseNoteItem (PTMI.ts:833-883) ซึ่ง PUTทั้ง record — ที่นี่ใช้ patch
 * เพื่อส่งเฉพาะ field ที่แก้จริง (กันข้อมูลของช่องที่ไม่มีใน UI V4 หายไป)
 */
const UpdateNurseNote: Handler<
  Ctx<"UPDATE_NURSE_NOTE">
> = async (controller, params) => {
  const ids = params.ids;
  const values = params.values ?? {};
  const changes = buildNurseNoteUpdatePayload({
    values,
    workingShift: params.workingShift,
    doctorOrderId: params.doctorOrderId,
  });

  if (params.noteId === undefined) {
    return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกรายการบันทึก" });
  }

  if (Object.keys(changes).length === 0) {
    return writeSlice(controller, params, { ids, error: "ไม่มีข้อความที่แก้เพื่อบันทึก" });
  }

  const auth = nurseAuthOf(controller, params);
  if (auth === null) return writeSlice(controller, params, { ids, error: NURSE_AUTH_MISSING });

  writeSlice(controller, params, { ids, saving: true, error: null });

  const result = await NurseNoteItemDetail.patch({
    pk: params.noteId,
    data: { ...changes, ...auth } as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadNurse(controller, { action: ACTIONS.LOAD_NURSE, card: params.card, ids });
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), nurseSavedMessage: "บันทึกการแก้ไขแล้ว", error: null });
};

/**
 * DELETE /apis/PTM/nurse-note-item/{pk}/ พร้อม {username, password} หรือ {user_token} ใน body
 * V3: PTMI.ts:885-900 -> ต้องใช้ SDK variant `NurseNoteItemDetail_apps_PTMM` (variant apps_PTM ไม่มี data)
 * verify จาก staging API แล้ว:
 *   DELETE โดยไม่มี body            -> 400 {"username":["ระบุข้อมูลในช่องนี้"],"password":[...]}
 *   DELETE body {username,password} ที่ไม่ถูกต้อง -> 400 {"__all__":["กรุณาใส่ ... ชื่อผู้ใช้ ...รหัสผ่านที่ถูกต้อง ..."]} (ไม่ได้ลบ)
 *   DELETE body {user_token} ที่ไม่ valid          -> 400 ["invalid user token"]
 * การลบแบบ soft (active=false) ใช้ไม่ได้: PATCH {active:false} ก็ 400 ขอ username/password เช่นกัน
 */
const DeleteNurseNote: Handler<
  Ctx<"DELETE_NURSE_NOTE">
> = async (controller, params) => {
  const ids = params.ids;

  if (params.noteId === undefined) {
    return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกรายการบันทึก" });
  }

  const auth = nurseAuthOf(controller, params);
  if (auth === null) return writeSlice(controller, params, { ids, error: NURSE_AUTH_MISSING });

  const result = await NurseNoteItemDetailWithBody.delete({
    pk: params.noteId,
    data: auth,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await LoadNurse(controller, { action: ACTIONS.LOAD_NURSE, card: params.card, ids });
  writeSlice(controller, params, { ids, saving: false, nurseSavedMessage: "ลบบันทึกแล้ว", error: null });
};

/* ============================================================== */
/*   SECTION 6b · FLAG ของ Patient Panel (ADR + problem list)      */
/* ============================================================== */

/**
 * โหลด flag ทั้งคู่หลังทุก write (paramsเดียวกับที่ LOAD ใช้:
 * ADR exclude_unused+severe_first, problem list is_active=true)
 */
const reloadFlags = async (
  controller: Ctl,
  params: { card?: string; action?: string; ids: ExamIds },
): Promise<void> => {
  const ids = params.ids;
  if (!ids.patientId) return;

  const [adr, problems] = await Promise.all([
    AdverseReactionList.list({
      params: allergyListParams(ids.patientId),
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    }),
    ProblemListList.list({
      params: problemListParams(ids.patientId),
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    }),
  ]);

  writeSlice(controller, params, {
    ids,
    allergyItems: allergyItemsFromApi(dataOf(adr)),
    problemItems: problemItemsFromApi(dataOf(problems)),
  });
};

const SaveAdr: Handler<Ctx<"SAVE_ADR">> = async (controller, params) => {
  const ids = params.ids;
  const note = `${params.note ?? ""}`.trim();

  if (!ids.patientId) return writeSlice(controller, params, { ids, error: "ยังไม่มีผู้ป่วย (patient id)" });
  if (note === "") return writeSlice(controller, params, { ids, error: "กรุณาระบุชื่อยา/อาการปัญหาก่อนบันทึก" });

  const result = await AdverseReactionList.create({
    data: buildAdrPayload({
      patientId: ids.patientId,
      note,
      action: ADR_ACTION_SAVE,
      adversaryType: params.adversaryType,
    }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await reloadFlags(controller, params);
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), error: null });
};

/** PATCH ต้องมี action+note ครู่กันเสมอ (verify: PATCH {note} อย่างเดียว -> 500) */
const UpdateAdr: Handler<Ctx<"UPDATE_ADR">> = async (controller, params) => {
  const ids = params.ids;
  const note = `${params.note ?? ""}`.trim();

  if (!params.adrId) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกประวัติแพ้" });
  if (note === "") return writeSlice(controller, params, { ids, error: "กรุณาระบุชื่อยา/อาการปัญหาก่อนบันทึก" });

  const result = await AdverseReactionDetail.patch({
    pk: params.adrId,
    data: buildAdrPayload({ note, action: ADR_ACTION_SAVE }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await reloadFlags(controller, params);
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), error: null });
};

/**
 * ปิดใช้งานประวัติแพ้: DELETE endpoint ตอบ 405 (verify แล้ว)
 * V3 จึงใช้ action CANCEL (Allergy.ts:312-322) -> status CANCELED และหายจาก list exclude_unused
 */
const CancelAdr: Handler<Ctx<"CANCEL_ADR">> = async (controller, params) => {
  const ids = params.ids;

  if (!params.adrId) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกประวัติแพ้" });

  /* PATCH ต้องได้ note ด้วย -> ใช้ note เดิมของ record นั้น */
  const current = sliceOf(controller).allergyItems.find((item) => item.id === params.adrId);
  const note = `${params.note ?? current?.note ?? ""}`.trim();

  const result = await AdverseReactionDetail.patch({
    pk: params.adrId,
    data: buildAdrPayload({ note, action: ADR_ACTION_CANCEL }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await reloadFlags(controller, params);
  writeSlice(controller, params, { ids, saving: false, error: null });
};

const SaveProblem: Handler<Ctx<"SAVE_PROBLEM">> = async (controller, params) => {
  const ids = params.ids;
  const message = `${params.message ?? ""}`.trim();

  if (!ids.patientId) return writeSlice(controller, params, { ids, error: "ยังไม่มีผู้ป่วย (patient id)" });
  if (message === "") return writeSlice(controller, params, { ids, error: "กรุณาระบุชื่อโรคประจำตัวก่อนบันทึก" });

  const result = await ProblemListList.create({
    data: buildProblemPayload({ patientId: ids.patientId, message }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await reloadFlags(controller, params);
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), error: null });
};

const UpdateProblem: Handler<Ctx<"UPDATE_PROBLEM">> = async (controller, params) => {
  const ids = params.ids;
  const message = `${params.message ?? ""}`.trim();

  if (!params.problemId) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกโรคประจำตัว" });
  if (message === "") return writeSlice(controller, params, { ids, error: "กรุณาระบุชื่อโรคประจำตัวก่อนบันทึก" });

  const result = await ProblemListDetail.patch({
    pk: params.problemId,
    data: buildProblemPayload({ message }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await reloadFlags(controller, params);
  writeSlice(controller, params, { ids, saving: false, savedAt: new Date().toISOString(), error: null });
};

/** ปิดใช้งานโรคประจำตัวด้วย is_active=false (verify: 200 และ list ?is_active=true ไม่คืนรายการอีก) */
const InactivateProblem: Handler<Ctx<"INACTIVATE_PROBLEM">> = async (controller, params) => {
  const ids = params.ids;

  if (!params.problemId) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกโรคประจำตัว" });

  const result = await ProblemListDetail.patch({
    pk: params.problemId,
    data: buildProblemPayload({ active: false }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await reloadFlags(controller, params);
  writeSlice(controller, params, { ids, saving: false, error: null });
};

/* ============================================================== */
/*   SECTION 6x · CHECK OUT (Patient Panel)                        */
/* ============================================================== */

/** ข้อมูล Check out ล่าสุดของ encounter */
const checkoutNow = async (controller: Ctl, encounterId: number) => {
  const detail = await EncounterDetail.retrieve({
    pk: encounterId,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  return checkoutEncounterOf(asDict(dataOf(detail)));
};

const patchEncounterAction = async (controller: Ctl, encounterId: number, action: string, note: string) =>
  errorMessageOf(
    await EncounterActionOnly.patch({
      pk: encounterId,
      data: { action, note },
      apiToken: controller.apiToken,
      extra: extraOf(controller),
    }),
  );

/**
 * Check out: บันทึกรูปแบบที่ EMR (ออกจากห้องตรวจ)
 * แล้วส่งต่อให้เป็น "รอชำระเงิน" พร้อมหมายเหตุ (CHECKED_OUT -> OPD_DISCHARGE -> WAIT_PAY)
 */
const CheckOut: Handler<Ctx<"CHECK_OUT">> = async (controller, params) => {
  const resolved = await resolveEmrId(controller, { ids: params.ids });
  const ids = resolved.ids;
  const finish = (error: string | null, checkout?: CheckoutEncounter): void => {
    writeSlice(controller, params, { ids, saving: false, error, ...(checkout && { checkout }) });
    params.onDone?.(error === null);
  };

  if (resolved.error || !ids.emrId || !ids.encounterId) {
    return finish(resolved.error ?? "ยังไม่มีเวชระเบียน/encounter ไม่สามารถ Check out ได้");
  }

  writeSlice(controller, params, { ids, saving: true, error: null });

  const discharged = await DoctorDischargePatient.update({
    pk: ids.emrId,
    data: {
      checkout_cause: params.checkoutCause ?? "",
      require_diagnosis: false,
      ...(params.deathDatetime && { death_datetime: params.deathDatetime }),
    } as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const dischargeError = errorMessageOf(discharged);
  if (dischargeError) return finish(`Check out ไม่สำเร็จ: ${dischargeError}`);

  // PATCH encounter ต้องมี action: ส่งหมายเหตุไปพร้อม OPD_DISCHARGE
  let error: string | null = null;
  if ((await checkoutNow(controller, ids.encounterId)).status === "CHECKED_OUT") {
    error = errorMessageOf(
      await EncounterDetail.patch({
        pk: ids.encounterId,
        data: { action: "OPD_DISCHARGE", discharge_note: (params.note ?? "").trim() } as never,
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    );
  }

  controller.handleEvent({
    message: "HandleRefreshEncounter",
    params: { encounterId: ids.encounterId },
  });
  finish(
    error ? `Check out แล้ว แต่บันทึกต่อไม่สำเร็จ: ${error}` : null,
    await checkoutNow(controller, ids.encounterId),
  );
};

/** ยกเลิก Check out: รอชำระเงิน -> ออกจากห้องตรวจ -> กำลังพบแพทย์ */
const CancelCheckOut: Handler<Ctx<"CANCEL_CHECK_OUT">> = async (controller, params) => {
  const ids = params.ids;
  const note = (params.note ?? "").trim();
  const finish = (error: string | null, checkout?: CheckoutEncounter): void => {
    writeSlice(controller, params, { ids, saving: false, error, ...(checkout && { checkout }) });
    params.onDone?.(error === null);
  };

  if (!ids.encounterId) return finish("ยังไม่ได้เลือกผู้ป่วย (encounter)");
  if (!note) return finish("กรุณาระบุเหตุผลที่ยกเลิก Check out");

  writeSlice(controller, params, { ids, saving: true, error: null });

  let error: string | null = null;
  if ((await checkoutNow(controller, ids.encounterId)).status === "WAIT_PAY") {
    error = await patchEncounterAction(controller, ids.encounterId, "OPD_UNDISCHARGE", note);
  }
  if (!error)
    error = await patchEncounterAction(controller, ids.encounterId, "OPD_CANCEL_CHECK_OUT", note);

  controller.handleEvent({
    message: "HandleRefreshEncounter",
    params: { encounterId: ids.encounterId },
  });
  finish(
    error ? `ยกเลิก Check out ไม่สำเร็จ: ${error}` : null,
    await checkoutNow(controller, ids.encounterId),
  );
};

/* ============================================================== */
/*   SECTION 7 · SEQUENCE ENTRY / DISPATCH                         */
/* ============================================================== */

const LOAD_METAS: SliceMeta[] = [
  { card: CARD_NAMES.doctor, action: ACTIONS.LOAD },
  { card: CARD_NAMES.gallery, action: ACTIONS.LOAD_GALLERY },
  { card: CARD_NAMES.nursing, action: ACTIONS.LOAD_NURSE },
];

export const Start: Handler<SeqState> = async (controller, params) => {
  /* restart: RunSequence ล้าง slice เหลือแค่ sequenceIndex (MainHISInterface.ts:676-712)
     Start จึงต้องสร้าง ids + defaults จาก params เอง */
  const ids = readIds(controller, params);

  controller.setState({
    ExamV4Sequence: { ...emptyExamV4Slice(), ids, sequenceIndex: "Action" },
  });
  setBtn(controller, LOAD_METAS, BLC_STATUS.LOADING);

  /* ข้อมูลของ Patient Panel ไม่ต้องรอเวชระเบียน */
  const panel = ids.patientId
    ? LoadPanel(controller, { action: ACTIONS.LOAD_PANEL, card: CARD_NAMES.panel, ids })
    : Promise.resolve();

  if (!ids.encounterId) {
    /* ไม่มี encounter -> ยังไม่ให้ card เรียก save ได้ เพราะ sequence เองก็มี emr ไม่ได้ */
    for (const meta of LOAD_METAS)
      reportError(controller, meta, "ยังไม่ได้เลือกผู้ป่วย (encounter)");
    return panel;
  }

  const resolved = await resolveEmrId(controller, { ids });
  if (resolved.error) {
    for (const meta of LOAD_METAS) {
      reportError(controller, meta, (resolved.error as string) ?? "โหลดข้อมูลเวชระเบียนไม่สำเร็จ");
    }
    return panel;
  }

  await Promise.all([
    /* ปุ่มเรียกเข้าตรวจของคิวส่ง action นี้มากับ restart: StartExam โหลดเวชระเบียนให้เองตอนจบ */
    params.action === ACTIONS.START_EXAM
      ? StartExam(controller, {
          action: ACTIONS.START_EXAM,
          card: CARD_NAMES.doctor,
          ids: resolved.ids,
          currentStatus: params.currentStatus,
        })
      : LoadExam(controller, { action: ACTIONS.LOAD, card: CARD_NAMES.doctor, ids: resolved.ids }),
    LoadGallery(controller, { action: ACTIONS.LOAD_GALLERY, card: CARD_NAMES.gallery, ids: resolved.ids }),
    LoadNurse(controller, { action: ACTIONS.LOAD_NURSE, card: CARD_NAMES.nursing, ids: resolved.ids }),
    panel,
  ]);
};

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionName]: Handler<Ctx<K>> }> = {
    [ACTIONS.LOAD]: LoadExam,
    [ACTIONS.LOAD_GALLERY]: LoadGallery,
    [ACTIONS.LOAD_GALLERY_HISTORY]: LoadGalleryHistory,
    [ACTIONS.LOAD_NURSE]: LoadNurse,
    [ACTIONS.LOAD_PANEL]: LoadPanel,
    [ACTIONS.START_EXAM]: StartExam,
    [ACTIONS.SAVE_RECORD]: SaveRecord,
    [ACTIONS.SAVE_HISTORY]: SaveHistory,
    [ACTIONS.LOAD_SENSITIVE_NOTE]: LoadSensitiveNote,
    [ACTIONS.SAVE_SENSITIVE_NOTE]: SaveSensitiveNote,
    [ACTIONS.SEARCH_ICD10]: SearchIcd10,
    [ACTIONS.UPLOAD_PHOTO]: UploadPhoto,
    [ACTIONS.UPDATE_PHOTO]: UpdatePhoto,
    [ACTIONS.DELETE_PHOTO]: DeletePhoto,
    [ACTIONS.SAVE_PHOTO_ANNOTATIONS]: SavePhotoAnnotations,
    [ACTIONS.DELETE_PHOTO_ANNOTATIONS]: DeletePhotoAnnotations,
    [ACTIONS.RESTORE_PHOTO]: RestorePhoto,
    [ACTIONS.LOAD_GALLERY_TEMPLATES]: LoadGalleryTemplates,
    [ACTIONS.SAVE_NURSE_NOTE]: SaveNurseNote,
    [ACTIONS.UPDATE_NURSE_NOTE]: UpdateNurseNote,
    [ACTIONS.DELETE_NURSE_NOTE]: DeleteNurseNote,
    [ACTIONS.SAVE_NURSE_TEMPLATE]: SaveNurseTemplate,
    [ACTIONS.DELETE_NURSE_TEMPLATE]: DeleteNurseTemplate,
    [ACTIONS.SAVE_ADR]: SaveAdr,
    [ACTIONS.UPDATE_ADR]: UpdateAdr,
    [ACTIONS.CANCEL_ADR]: CancelAdr,
    [ACTIONS.SAVE_PROBLEM]: SaveProblem,
    [ACTIONS.UPDATE_PROBLEM]: UpdateProblem,
    [ACTIONS.INACTIVATE_PROBLEM]: InactivateProblem,
    [ACTIONS.CHECK_OUT]: CheckOut,
    [ACTIONS.CANCEL_CHECK_OUT]: CancelCheckOut,
  };

  const { action } = params;
  /* ActionType ไม่มี field card (card มาจาก params ของ card ผู้ส่ง) จึงอ่านผ่าน meta cast */
  const meta: SliceMeta = { card: (params as SliceMeta).card, action };
  const handler = actionHandlers[action as ActionName] as Handler<Ctx<ActionName>> | undefined;

  if (handler) {
    return handler(controller, { ...params, action: action as ActionName, ids: readIds(controller, params) });
  }

  console.error(`No handler found for action: ${action}`);

  return reportError(controller, meta, `ไม่รู้จัก action "${action}" ใน ${SEQUENCE_NAME}`);
};
